import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MdPeople } from "react-icons/md";
import Avatar from "components/avatar";
import Badge from "components/badge";
import Button from "components/button";
import { CardHeader } from "components/card";
import DataTable from "components/data-table";
import { SearchInput } from "components/fields";
import { EmptyState } from "components/states";
import { profilePath } from "components/assessment/audiences";
import { getArchetype } from "lib/archetypes";
import { EMPTY, formatDate } from "utils/format";

const ArchetypePill = ({ archetype }) => {
  if (!archetype) return <span className="text-fg-muted">{EMPTY}</span>;
  return (
    <Badge tone="archetype" archetype={archetype}>
      {getArchetype(archetype)?.label || archetype}
    </Badge>
  );
};

const searchText = (person) =>
  [
    person.name,
    person.email,
    person.role,
    getArchetype(person.primaryArchetype)?.label,
    getArchetype(person.secondaryArchetype)?.label,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const COLUMNS = [
  {
    accessorKey: "name",
    header: "Name",
    cell: ({ row }) => (
      <div className="flex items-center gap-3">
        <Avatar user={{ avatar: row.original.avatar }} name={row.original.name} size="sm" />
        <div className="min-w-0">
          <p className="truncate font-medium text-fg">{row.original.name}</p>
          {row.original.email && (
            <p className="truncate text-xs text-fg-muted">{row.original.email}</p>
          )}
        </div>
      </div>
    ),
  },
  {
    accessorKey: "role",
    header: "Role",
    cell: ({ getValue }) =>
      getValue() ? (
        <Badge tone="neutral" className="capitalize">
          {getValue()}
        </Badge>
      ) : (
        <span className="text-fg-muted">{EMPTY}</span>
      ),
  },
  {
    accessorKey: "primaryArchetype",
    header: "Primary Archetype",
    cell: ({ getValue }) => <ArchetypePill archetype={getValue()} />,
  },
  {
    accessorKey: "secondaryArchetype",
    header: "Secondary",
    cell: ({ getValue }) => <ArchetypePill archetype={getValue()} />,
  },
  {
    accessorKey: "lastAssessmentAt",
    header: "Last Assessment",
    cell: ({ getValue }) =>
      getValue() ? (
        <span className="whitespace-nowrap">{formatDate(getValue())}</span>
      ) : (
        <span className="text-fg-muted">Never</span>
      ),
  },
];

const PeopleDirectory = ({ people, config }) => {
  const navigate = useNavigate();
  const { labels } = config;
  const [query, setQuery] = useState("");
  const [sorting, setSorting] = useState([]);

  const rows = useMemo(() => {
    const list = people || [];
    const q = query.trim().toLowerCase();
    return q ? list.filter((person) => searchText(person).includes(q)) : list;
  }, [people, query]);

  const heading = `${labels.adjective} Directory`;
  const hasPeople = (people || []).length > 0;

  const emptyState = hasPeople ? (
    <EmptyState
      variant="filtered"
      title="No results match your search"
      action={
        <Button variant="ghost" onClick={() => setQuery("")}>
          Clear Search
        </Button>
      }
    />
  ) : (
    <EmptyState
      icon={MdPeople}
      title={`No ${labels.plural} Assessed Yet`}
      description={`${labels.plural} appear here once they complete an assessment.`}
    />
  );

  return (
    <section className="flex flex-col gap-4">
      <CardHeader
        title={heading}
        actions={
          <SearchInput
            value={query}
            onChange={setQuery}
            placeholder={`Search ${labels.pluralLower}…`}
            ariaLabel={`Search ${labels.pluralLower}`}
            className="w-full sm:w-64"
          />
        }
      />
      <DataTable
        aria-label={heading}
        columns={COLUMNS}
        data={rows}
        getRowId={(person) => String(person.userId)}
        sorting={sorting}
        onSortingChange={setSorting}
        onRowClick={(person) => navigate(profilePath(config, person.userId))}
        emptyState={emptyState}
      />
    </section>
  );
};

export default PeopleDirectory;
